import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";

import { getInitialDragOffset } from "./helpers";
import { SquareImageEditorStateless, SquareImageEditorStatelessHandle } from "./SquareImageEditor.stateless";
import { useImageDrag } from "./useImageDrag";
import { useSquareImagePreparation } from "./useSquareImagePreparation";
import { useThrottledPreviewUpdate } from "./useThrottledPreviewUpdate";

import { generateFitModeCrop, generateSquareCrop } from "@/components/ImageUploader/utils";

export interface SquareImageEditorProps {
  imageDataUrl: string;
  targetSize?: number;
  previewSize?: number;
  onConfirm: (finalImageDataUrl: string) => void;
  onPreviewUpdate?: (previewDataUrl: string) => void;
  onReplace?: () => void;
  onDelete?: () => void;
  preserveTransparency?: boolean;
  showCenterFitButton?: boolean;
  useWhiteBackground?: boolean;
  onFitToggle?: (isFitMode: boolean) => void;
}

export type SquareImageEditorHandle = {
  confirm: () => Promise<void>;
};

export const SquareImageEditor = forwardRef<SquareImageEditorHandle, SquareImageEditorProps>(
  (
    {
      imageDataUrl,
      targetSize = 800,
      previewSize,
      onConfirm,
      onPreviewUpdate,
      onReplace,
      onDelete,
      preserveTransparency,
      showCenterFitButton,
      useWhiteBackground,
      onFitToggle,
    },
    ref
  ) => {
    const [isFitMode, setIsFitMode] = useState(false);
    const [viewportSize, setViewportSize] = useState(targetSize);
    const statelessRef = useRef<SquareImageEditorStatelessHandle>(null);

    const { aspectRatio, dragOffset, fittedImage, isLoading, setDragOffset } = useSquareImagePreparation({
      imageDataUrl,
      targetSize,
    });

    const editorState = {
      aspectRatio,
      dragOffset,
      fittedImage,
      imageDataUrl,
      isFitMode,
      isLoading,
      targetSize,
      useWhiteBackground,
    };

    const { schedulePreviewUpdate } = useThrottledPreviewUpdate({
      editorState,
      onPreviewUpdate,
      previewSize,
    });

    const { handleDragStart } = useImageDrag({
      aspectRatio,
      dragOffset,
      fittedImage,
      onDragChange: setDragOffset,
      schedulePreviewUpdate,
      targetSize,
      viewportSize,
    });

    const handleConfirm = useCallback(async () => {
      if (!fittedImage) {
        onConfirm(imageDataUrl);
        return;
      }

      const finalImageDataUrl =
        isFitMode && aspectRatio && aspectRatio !== "square"
          ? await generateFitModeCrop(
              imageDataUrl,
              aspectRatio,
              targetSize,
              preserveTransparency,
              undefined,
              useWhiteBackground
            )
          : await generateSquareCrop(
              fittedImage.dataUrl,
              fittedImage.width,
              fittedImage.height,
              dragOffset,
              targetSize
            );

      onConfirm(finalImageDataUrl);
    }, [
      fittedImage,
      imageDataUrl,
      dragOffset,
      targetSize,
      onConfirm,
      isFitMode,
      aspectRatio,
      useWhiteBackground,
      preserveTransparency,
    ]);

    const handleFit = useCallback(() => {
      if (!fittedImage || !aspectRatio) {
        return;
      }
      setIsFitMode((prev) => {
        const newFitMode = !prev;
        onFitToggle?.(newFitMode);
        return newFitMode;
      });
      const centeredOffset = getInitialDragOffset(aspectRatio, fittedImage, targetSize);
      setDragOffset(centeredOffset);
    }, [aspectRatio, fittedImage, targetSize, setDragOffset, onFitToggle]);

    useEffect(() => {
      setIsFitMode(false);
    }, [imageDataUrl]);

    useEffect(() => {
      if (!isLoading && fittedImage) {
        schedulePreviewUpdate();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFitMode]);

    const handleViewportSizeChange = useCallback((size: number) => {
      setViewportSize(size);
    }, []);

    // Imperative handle for parent to trigger confirm
    useImperativeHandle(
      ref,
      () => ({
        confirm: () => handleConfirm(),
      }),
      [handleConfirm]
    );

    return (
      <SquareImageEditorStateless
        ref={statelessRef}
        imageDataUrl={imageDataUrl}
        fittedImage={fittedImage}
        aspectRatio={aspectRatio}
        dragOffset={dragOffset}
        isFitMode={isFitMode}
        isLoading={isLoading}
        targetSize={targetSize}
        onDragStart={handleDragStart}
        onFit={handleFit}
        onReplace={onReplace}
        onDelete={onDelete}
        onConfirm={handleConfirm}
        onViewportSizeChange={handleViewportSizeChange}
        showCenterFitButton={showCenterFitButton}
        useWhiteBackground={useWhiteBackground}
      />
    );
  }
);

SquareImageEditor.displayName = "SquareImageEditor";
